import React from 'react';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/styles';
import { validateParentPlan, getPlanNumber } from '../utils/planHelpers';

const useStyles = makeStyles(() => ({
  formWrapper: {
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    borderRadius: '8px',
    padding: '25px',
    marginBottom: '30px'
  },
  formGroup: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    fontWeight: '600',
    fontSize: '14px',
    marginBottom: '8px',
    color: '#032A37'
  },
  currentValue: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '8px'
  },
  select: {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    backgroundColor: '#fff',
    '&:focus': {
      outline: 'none',
      borderColor: '#032A37'
    }
  },
  errorMessage: {
    color: '#d32f2f',
    fontSize: '12px',
    marginTop: '5px'
  }
}));

const PlanSelectionForm = ({
  currentNuclearPlan,
  currentParentPlan,
  selectedNuclearPlan,
  selectedParentPlan,
  onNuclearPlanChange,
  onParentPlanChange,
  plansChanged,
  availablePlans
}) => {
  const classes = useStyles();

  // Generate plan options (Plan 1 - Plan 7)
  const planOptions = availablePlans || [
    { code: 'Plan_1', name: 'Plan 1' },
    { code: 'Plan_2', name: 'Plan 2' },
    { code: 'Plan_3', name: 'Plan 3' },
    { code: 'Plan_4', name: 'Plan 4' },
    { code: 'Plan_5', name: 'Plan 5' },
    { code: 'Plan_6', name: 'Plan 6' },
    { code: 'Plan_7', name: 'Plan 7' }
  ];

  // Filter parent plan options based on selected nuclear plan
  const selectedNuclearPlanNum = getPlanNumber(selectedNuclearPlan);
  const parentPlanOptions = planOptions.filter(plan => {
    const planNum = getPlanNumber(plan.code);
    return planNum <= selectedNuclearPlanNum;
  });

  // Validate parent plan selection
  const validation = validateParentPlan(selectedNuclearPlan, selectedParentPlan);

  const currentNuclearPlanNum = getPlanNumber(currentNuclearPlan);
  const currentParentPlanNum = getPlanNumber(currentParentPlan);

  return (
    <div className={classes.formWrapper}>
      <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '20px', color: '#032A37' }}>
        Select New Plans
      </h3>

      <Grid container spacing={3}>
        {/* Nuclear Plan Selection */}
        <Grid item xs={12} md={6}>
          <div className={classes.formGroup}>
            <label className={classes.label}>Nuclear Plan</label>
            <div className={classes.currentValue}>
              Current: Plan {currentNuclearPlanNum}
            </div>
            <select
              className={classes.select}
              value={selectedNuclearPlan}
              onChange={(e) => onNuclearPlanChange(e.target.value)}
            >
              <option value="">-- Select Nuclear Plan --</option>
              {planOptions.map(plan => (
                <option key={plan.code} value={plan.code}>
                  {plan.name}
                </option>
              ))}
            </select>
          </div>
        </Grid>

        {/* Parent Plan Selection */}
        <Grid item xs={12} md={6}>
          <div className={classes.formGroup}>
            <label className={classes.label}>Parent Plan</label>
            <div className={classes.currentValue}>
              Current: {currentParentPlanNum ? `Plan ${currentParentPlanNum}` : 'Not Set'}
            </div>
            <select
              className={classes.select}
              value={selectedParentPlan}
              onChange={(e) => onParentPlanChange(e.target.value)}
              disabled={!selectedNuclearPlan}
            >
              <option value="">-- Select Parent Plan --</option>
              {parentPlanOptions.map(plan => (
                <option key={plan.code} value={plan.code}>
                  {plan.name}
                </option>
              ))}
            </select>
            {!selectedNuclearPlan && (
              <div className={classes.errorMessage}>
                Please select nuclear plan first
              </div>
            )}
            {!validation.isValid && (
              <div className={classes.errorMessage}>
                {validation.message}
              </div>
            )}
          </div>
        </Grid>
      </Grid>

      {selectedNuclearPlan && selectedParentPlan && validation.isValid && plansChanged && (
        <div style={{
          marginTop: '20px',
          padding: '12px',
          backgroundColor: '#d4edda',
          border: '1px solid #c3e6cb',
          borderRadius: '4px',
          color: '#155724',
          fontSize: '13px'
        }}>
          ✓ Plan selection valid: Nuclear Plan {getPlanNumber(selectedNuclearPlan)} with Parent Plan {getPlanNumber(selectedParentPlan)}
        </div>
      )}
    </div>
  );
};

export default PlanSelectionForm;
